export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Human-readable message from an error body. Zod 422s arrive as
 * { error: 'Validation failed', issues: [...] } — surface the first issue
 * ("invoicePrefix: Prefix may only contain…") instead of the generic label.
 */
function errorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const body = data as { error?: unknown; issues?: { path?: (string | number)[]; message?: string }[] };
  const issue = Array.isArray(body.issues) ? body.issues[0] : undefined;
  if (issue?.message) {
    const path = issue.path?.filter((p) => typeof p === 'string').join('.');
    return path ? `${path}: ${issue.message}` : issue.message;
  }
  return body.error !== undefined ? String(body.error) : fallback;
}

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await res.json() : undefined;
  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(data, res.statusText), data);
  }
  return data as T;
}

async function upload<T>(url: string, body: FormData): Promise<T> {
  const res = await fetch(url, { method: 'POST', body, credentials: 'include' });
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const data = isJson ? await res.json() : undefined;
  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(data, res.statusText), data);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => req<T>('GET', url),
  post: <T>(url: string, body?: unknown) => req<T>('POST', url, body),
  put: <T>(url: string, body?: unknown) => req<T>('PUT', url, body),
  patch: <T>(url: string, body?: unknown) => req<T>('PATCH', url, body),
  del: <T>(url: string) => req<T>('DELETE', url),
  upload: <T>(url: string, body: FormData) => upload<T>(url, body),
};
